import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle2, Plus, Trash2 } from 'lucide-react';
import { useQuoteModal } from '../hooks/useQuoteModal';
import { Button } from './ui/Button';
import { sendQuoteRequest } from '../lib/emailjs';
import { fetchContentfulEntriesWithIncludes, resolveLinkedEntries } from '../lib/contentful';
const FALLBACK_CATEGORIES = ['Web & Mobile Development', 'Design & Branding', 'Digital Marketing', 'Corporate Media & Video', 'Consultancy & Strategy', 'Other'];
interface ServiceSelection {
  id: string;
  category: string;
  specialty: string;
  description: string;
  categoryIsOther: boolean;
  specialtyIsOther: boolean;
}
export function QuoteModal() {
  const {
    isOpen,
    closeModal
  } = useQuoteModal();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState<any[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [selections, setSelections] = useState<ServiceSelection[]>([{
    id: '1',
    category: '',
    specialty: '',
    description: '',
    categoryIsOther: false,
    specialtyIsOther: false
  }]);
  const [brief, setBrief] = useState({
    description: '',
    timeline: '',
    budget: ''
  });
  const [details, setDetails] = useState({
    name: '',
    email: '',
    phone: '',
    company: ''
  });
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      if (categories.length === 0) {
        loadCategories();
      }
    } else {
      document.body.style.overflow = 'unset';
      setTimeout(() => {
        setStep(1);
        setSelections([{
          id: '1',
          category: '',
          specialty: '',
          description: '',
          categoryIsOther: false,
          specialtyIsOther: false
        }]);
        setBrief({
          description: '',
          timeline: '',
          budget: ''
        });
        setDetails({
          name: '',
          email: '',
          phone: '',
          company: ''
        });
        setError('');
      }, 300);
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  // ESC key handler
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
    };
    if (isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, closeModal]);
  const loadCategories = async () => {
    setLoadingCategories(true);
    try {
      const {
        items,
        includes
      } = await fetchContentfulEntriesWithIncludes('services');
      const mapped = items.map(item => {
        const specs = resolveLinkedEntries(item.fields.serviceSpecialities, includes);
        return {
          name: item.fields.serviceName,
          specialties: specs.map(s => s.fields.title || s.fields.name)
        };
      });
      setCategories(mapped);
    } catch (err) {
      console.error('Failed to load categories', err);
      setCategories(FALLBACK_CATEGORIES.map(c => ({
        name: c,
        specialties: []
      })));
    } finally {
      setLoadingCategories(false);
    }
  };
  const updateSelection = (id: string, updates: Partial<ServiceSelection>) => {
    setSelections(prev => prev.map(s => {
      if (s.id !== id) return s;
      const next = {
        ...s,
        ...updates
      };
      // Reset specialty when category changes
      if ('category' in updates && updates.category !== s.category) {
        next.specialty = '';
        next.specialtyIsOther = false;
      }
      return next;
    }));
  };
  const handleCategoryChange = (id: string, value: string) => {
    if (value === 'Other') {
      updateSelection(id, {
        category: '',
        categoryIsOther: true
      });
    } else {
      updateSelection(id, {
        category: value,
        categoryIsOther: false
      });
    }
  };
  const handleSpecialtyChange = (id: string, value: string) => {
    if (value === 'Other') {
      updateSelection(id, {
        specialty: '',
        specialtyIsOther: true
      });
    } else {
      updateSelection(id, {
        specialty: value,
        specialtyIsOther: false
      });
    }
  };
  const addSelection = () => {
    setSelections(prev => [...prev, {
      id: Date.now().toString(),
      category: '',
      specialty: '',
      description: '',
      categoryIsOther: false,
      specialtyIsOther: false
    }]);
  };
  const removeSelection = (id: string) => {
    setSelections(prev => prev.filter(s => s.id !== id));
  };
  const handleNext = () => {
    if (step < 4) setStep(step + 1);
  };
  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError('');
    try {
      const formattedServices = selections.map(s => {
        const cat = s.category || '';
        const spec = s.specialty ? ` → ${s.specialty}` : '';
        const desc = s.description ? `: ${s.description}` : '';
        return `${cat}${spec}${desc}`;
      }).filter(Boolean).join(' | ');
      const formattedSpecialties = selections.map(s => s.specialty).filter(Boolean).join(', ');
      await sendQuoteRequest({
        name: details.name,
        email: details.email,
        phone: details.phone,
        company: details.company,
        services: formattedServices,
        specialties: formattedSpecialties,
        description: brief.description,
        timeline: brief.timeline,
        budget: brief.budget
      });
      setStep(4);
    } catch (err) {
      setError('Something went wrong. Please try again or email us directly at biz@belo.co.ke');
    } finally {
      setIsSubmitting(false);
    }
  };
  const isStep1Valid = selections.some(s => s.category !== '');
  // Step 2 is always valid — description is optional since they may have described per-service
  const isStep2Valid = true;
  const isStep3Valid = details.name.trim() !== '' && details.email.trim() !== '' && details.phone.trim() !== '';
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-91">
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-id="element-92">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={closeModal} data-id="element-93" />

        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-94">
          {/* Header & Progress */}
          {step < 4 && <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-white shrink-0" data-id="element-95">
              <div className="flex items-center gap-2" data-id="element-96">
                {[1, 2, 3].map(i => <div key={i} className="flex items-center" data-id="element-97">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${step >= i ? 'bg-[#00316B] text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-98">
                      {i}
                    </div>
                    {i < 3 && <div className={`w-8 h-1 mx-1 rounded-full transition-colors ${step > i ? 'bg-[#00316B]' : 'bg-gray-100'}`} data-id="element-99" />}
                  </div>)}
              </div>
              <button onClick={closeModal} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors" data-id="element-100">
                <X className="w-5 h-5" data-id="element-101" />
              </button>
            </div>}

          {/* Content Area */}
          <div className="p-6 sm:p-8 overflow-y-auto flex-1" data-id="element-102">
            {step === 1 && <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-6" data-id="element-103">
                <div data-id="element-104">
                  <h2 className="text-2xl font-bold font-heading text-[#0C0C0C] mb-2" data-id="element-105">
                    What services are you interested in?
                  </h2>
                  <p className="text-gray-600" data-id="element-106">
                    Select the areas where you need our expertise.
                  </p>
                </div>

                <div className="space-y-6" data-id="element-107">
                  {selections.map((sel, index) => {
                const selectedCat = categories.find(c => c.name === sel.category);
                const specialties = selectedCat?.specialties || [];
                const showSpecialtyDropdown = sel.category && sel.category !== 'Other' && !sel.categoryIsOther;
                return <div key={sel.id} className="p-4 bg-gray-50 rounded-xl border border-gray-100 relative" data-id="element-108">
                        {index > 0 && <button onClick={() => removeSelection(sel.id)} className="absolute top-4 right-4 text-gray-400 hover:text-red-500 transition-colors" data-id="element-109">
                            <Trash2 className="w-4 h-4" data-id="element-110" />
                          </button>}
                        <div className="space-y-4" data-id="element-111">
                          {/* Category dropdown or text input */}
                          <div data-id="element-112">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-113">
                              Service category
                            </label>
                            {loadingCategories ? <div className="w-full h-12 bg-gray-200 animate-pulse rounded-lg" data-id="element-114" /> : sel.categoryIsOther ? <div className="relative" data-id="element-115">
                                <input type="text" value={sel.category} onChange={e => updateSelection(sel.id, {
                          category: e.target.value
                        })} placeholder="Type your service category..." autoFocus className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00316B]/20 focus:border-[#00316B] bg-white pr-24" data-id="element-116" />
                                <button type="button" onClick={() => updateSelection(sel.id, {
                          category: '',
                          categoryIsOther: false
                        })} className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#00316B] hover:text-[#FF6600] font-medium transition-colors" data-id="element-117">
                                  ← Back to list
                                </button>
                              </div> : <select value={sel.category} onChange={e => handleCategoryChange(sel.id, e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00316B]/20 focus:border-[#00316B] bg-white" data-id="element-118">
                                <option value="" data-id="element-119">Choose a category...</option>
                                {categories.map(c => <option key={c.name} value={c.name} data-id="element-120">
                                    {c.name}
                                  </option>)}
                                <option value="Other" data-id="element-121">
                                  Other (not listed)
                                </option>
                              </select>}
                          </div>

                          {/* Specialty dropdown or text input — only when a real category is selected */}
                          {showSpecialtyDropdown && <div data-id="element-122">
                              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-123">
                                Service specialty
                              </label>
                              {sel.specialtyIsOther ? <div className="relative" data-id="element-124">
                                  <input type="text" value={sel.specialty} onChange={e => updateSelection(sel.id, {
                          specialty: e.target.value
                        })} placeholder="Type your service specialty..." autoFocus className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00316B]/20 focus:border-[#00316B] bg-white pr-24" data-id="element-125" />
                                  <button type="button" onClick={() => updateSelection(sel.id, {
                          specialty: '',
                          specialtyIsOther: false
                        })} className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#00316B] hover:text-[#FF6600] font-medium transition-colors" data-id="element-126">
                                    ← Back to list
                                  </button>
                                </div> : <select value={sel.specialty} onChange={e => handleSpecialtyChange(sel.id, e.target.value)} disabled={loadingCategories} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00316B]/20 focus:border-[#00316B] bg-white disabled:bg-gray-100 disabled:text-gray-400" data-id="element-127">
                                  <option value="" data-id="element-128">
                                    Choose a specialty...
                                  </option>
                                  {specialties.map((s: string) => <option key={s} value={s} data-id="element-129">
                                      {s}
                                    </option>)}
                                  <option value="Other" data-id="element-130">
                                    Other (not listed)
                                  </option>
                                </select>}
                            </div>}

                          {/* Describe this service — always available once a category is picked */}
                          {sel.category && <div data-id="element-131">
                              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-132">
                                Describe this service
                              </label>
                              <textarea value={sel.description} onChange={e => updateSelection(sel.id, {
                        description: e.target.value
                      })} placeholder="Tell us more about what you need for this service (press Enter for a new line)..." rows={2} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00316B]/20 focus:border-[#00316B] text-sm" data-id="element-133" />
                            </div>}
                        </div>
                      </div>;
              })}
                </div>

                <button onClick={addSelection} className="flex items-center gap-2 text-[#00316B] font-medium hover:text-[#FF6600] transition-colors text-sm" data-id="element-134">
                  <Plus className="w-4 h-4" data-id="element-135" />
                  Add another service
                </button>
              </motion.div>}

            {step === 2 && <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-6" data-id="element-136">
                <div data-id="element-137">
                  <h2 className="text-2xl font-bold font-heading text-[#0C0C0C] mb-2" data-id="element-138">
                    Project overview
                  </h2>
                  <p className="text-gray-600" data-id="element-139">
                    Share any additional context about the overall project.
                  </p>
                </div>

                <div className="space-y-5" data-id="element-140">
                  <div data-id="element-141">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-142">
                      Overall project description
                    </label>
                    <textarea value={brief.description} onChange={e => setBrief({
                  ...brief,
                  description: e.target.value
                })} placeholder="Any additional context about the overall project, goals, or requirements..." rows={4} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00316B]/20 focus:border-[#00316B] resize-none" data-id="element-143" />
                    <p className="text-xs text-gray-400 mt-1" data-id="element-144">
                      Optional if you've already described each service above
                    </p>
                  </div>

                  <div data-id="element-145">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-146">
                      Project timeline
                    </label>
                    <input type="text" value={brief.timeline} onChange={e => setBrief({
                  ...brief,
                  timeline: e.target.value
                })} placeholder="e.g. We need this live by end of April" className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00316B]/20 focus:border-[#00316B]" data-id="element-147" />
                  </div>

                  <div data-id="element-148">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-149">
                      Approximate budget
                    </label>
                    <input type="text" value={brief.budget} onChange={e => setBrief({
                  ...brief,
                  budget: e.target.value
                })} placeholder="e.g. KES 150,000 or We're flexible" className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00316B]/20 focus:border-[#00316B]" data-id="element-150" />
                    <p className="text-xs text-gray-400 mt-1" data-id="element-151">
                      This helps us tailor the right solution — no commitment
                      required
                    </p>
                  </div>
                </div>
              </motion.div>}

            {step === 3 && <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-6" data-id="element-152">
                <div data-id="element-153">
                  <h2 className="text-2xl font-bold font-heading text-[#0C0C0C] mb-2" data-id="element-154">
                    Your contact information
                  </h2>
                  <p className="text-gray-600" data-id="element-155">
                    How should we reach you with the quote?
                  </p>
                </div>

                <div className="space-y-5" data-id="element-156">
                  <div data-id="element-157">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-158">
                      Full name *
                    </label>
                    <input type="text" value={details.name} onChange={e => setDetails({
                  ...details,
                  name: e.target.value
                })} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00316B]/20 focus:border-[#00316B]" data-id="element-159" />
                  </div>

                  <div data-id="element-160">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-161">
                      Email address *
                    </label>
                    <input type="email" value={details.email} onChange={e => setDetails({
                  ...details,
                  email: e.target.value
                })} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00316B]/20 focus:border-[#00316B]" data-id="element-162" />
                  </div>

                  <div data-id="element-163">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-164">
                      Phone number *
                    </label>
                    <input type="tel" value={details.phone} onChange={e => setDetails({
                  ...details,
                  phone: e.target.value
                })} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00316B]/20 focus:border-[#00316B]" data-id="element-165" />
                  </div>

                  <div data-id="element-166">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-167">
                      Company or organisation name
                    </label>
                    <input type="text" value={details.company} onChange={e => setDetails({
                  ...details,
                  company: e.target.value
                })} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00316B]/20 focus:border-[#00316B]" data-id="element-168" />
                  </div>
                </div>

                {error && <p className="text-red-500 text-sm mt-2" data-id="element-169">{error}</p>}
              </motion.div>}

            {step === 4 && <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="flex flex-col items-center justify-center py-12 text-center" data-id="element-170">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6" data-id="element-171">
                  <CheckCircle2 className="w-10 h-10 text-green-600" data-id="element-172" />
                </div>
                <h2 className="text-3xl font-bold font-heading text-[#0C0C0C] mb-4" data-id="element-173">
                  We've received your brief
                </h2>
                <p className="text-gray-600 text-lg max-w-md mx-auto mb-8" data-id="element-174">
                  Our team will review your request and get back to you with a
                  detailed quote within a few hours.
                </p>
                <Button onClick={closeModal} size="lg" data-id="element-175">
                  Done
                </Button>
              </motion.div>}
          </div>

          {/* Footer Navigation */}
          {step < 4 && <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between shrink-0" data-id="element-176">
              {step > 1 ? <Button variant="ghost" onClick={handleBack} disabled={isSubmitting} data-id="element-177">
                  Back
                </Button> : <div data-id="element-178" />}

              {step < 3 ? <Button onClick={handleNext} disabled={step === 1 && !isStep1Valid} data-id="element-179">
                  Next Step
                </Button> : <Button onClick={handleSubmit} isLoading={isSubmitting} disabled={!isStep3Valid} data-id="element-180">
                  Submit Request
                </Button>}
            </div>}
        </motion.div>
      </div>
    </AnimatePresence>;
}